import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Select, InputNumber, Card, Typography, App } from 'antd';
import { getMenuById, getAllMenus, updateMenu, createMenu } from '../../services/menuService';
import type { MenuItem } from '../../types/menu';

const { Title } = Typography;
const { Option } = Select;

const MenuForm: React.FC = () => {
  const [form] = Form.useForm();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const isEditing = !!id;
  const { message } = App.useApp();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const menuRes = await getAllMenus();
        setMenuItems(menuRes.data.data || []);

        if (isEditing && id) {
          const itemRes = await getMenuById(id);
          const itemData = itemRes.data.data;
          if (itemData) {
            form.setFieldsValue(itemData);
          }
        }
      } catch (error) {
        message.error('Failed to load data');
      }
    };

    fetchData();
  }, [id, isEditing, form, message]);

  const onFinish = async (values: any) => {
    try {
      if (isEditing && id) {
        await updateMenu(id, values);
        message.success('Menu item updated successfully');
      } else {
        await createMenu(values);
        message.success('Menu item created successfully');
      }
      navigate('/admin/menus');
    } catch (error) {
      message.error('Failed to save menu item');
    }
  };

  return (
    <Card>
      <Title level={3}>{isEditing ? 'Edit Menu Item' : 'Create New Menu Item'}</Title>
      <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ Order: 10 }}>
        <Form.Item name="Name" label="Menu Name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="Path" label="URL Path">
          <Input placeholder="e.g., /admin/dashboard (leave empty for parent menu)" />
        </Form.Item>
        <Form.Item name="Icon" label="Icon Name">
          <Input placeholder="e.g., DashboardOutlined (from Ant Design)" />
        </Form.Item>
        <Form.Item name="Order" label="Order No" rules={[{ required: true, type: 'number' }]}>
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="ParentID" label="Parent Menu">
          <Select allowClear>
            <Option value={null}>— None (Root Level) —</Option>
            {menuItems.map(item => (
                <Option key={item.id} value={item.id}>
                  {item.Name}
                </Option>
              ))}
          </Select>
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" style={{ marginRight: 8 }}>
            {isEditing ? 'Save Changes' : 'Create Menu'}
          </Button>
          <Button onClick={() => navigate('/admin/menus')}>
            Cancel
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default MenuForm;
